import {Money} from "../../stem-core/localization/Money";
import {StemDate} from "../../stem-core/time/Date";
import {Merchant} from "./MerchantStore";
import {PaymentMethod} from "./PaymentMethodStore";
import {Currency} from "../../stem-core/localization/CurrencyStore";
import {UserAddress} from "./UserAddressStore";
import {type UserProfile} from "./UserProfileStore";
import {field} from "../../stem-core/state/StoreField";
import {BaseEnum, makeEnum} from "../../stem-core/state/BaseEnum";
import {TimeUnit} from "../../stem-core/time/Duration";
import {capitalize} from "../../stem-core/base/Utils";
import {MerchantConversion} from "./merchant/MerchantConversionStore"; // For dependency order
import {PaymentStatusDependentStore} from "./misc/StoreUtils";  // For visibility in Dashboard.


@makeEnum
export class CycleUnit extends BaseEnum {
    static ONE_TIME = {timeUnit: null};
    static DAY = {timeUnit: TimeUnit.DAY};
    static WEEK = {timeUnit: TimeUnit.WEEK, abrev: "wk"};
    static MONTH = {timeUnit: TimeUnit.MONTH, abrev: "mo"};
    static YEAR = {timeUnit: TimeUnit.YEAR, abrev: "yr"};

    // Returns "Monthly"/"Yearly", etc
    getFrequencyName(): string {
        return this.frequencyName || capitalize(this.timeUnit?.getFrequencyName() || this.getName());
    }

    getAbrev(): string {
        return this.abrev || this.value;
    }

    isOneTime(): boolean {
        return this === CycleUnit.ONE_TIME;
    }

    static allRecurring(): CycleUnit[] {
        return this.all().filter(cu => !cu.isOneTime());
    }
}


export class RecurringPaymentStoreObject extends PaymentStatusDependentStore {
    @field(Number) userId: number;
    @field("UserProfile") user: UserProfile;
    @field(String) blinkSignature: string;
    @field(Date) createdAt: StemDate;
    @field(Date) canceledAt: StemDate;
    @field(Date) paidUntil: StemDate;
    @field(Date) activeUntil: StemDate;
    @field(Date) nextPaymentAttempt: StemDate;
    @field(Date) lastSuccessfulPayment: StemDate;
    @field("Merchant") merchant: Merchant;
    @field(Number) amount: number;
    @field("Currency") currency: Currency;
    @field(Number) deliveryAddressId: number;
    @field("UserAddress") deliveryAddress: UserAddress;
    @field(Number) billingAddressId: number;
    @field("UserAddress") billingAddress: UserAddress;
    @field(Number) merchantId: number;
    @field(Number) preferredPaymentMethodId: number;
    @field(String) status: typeof RecurringPaymentStoreObject.Status[keyof typeof RecurringPaymentStoreObject.Status];
    @field("MerchantConversion") conversion: MerchantConversion;

    // TODO @Mihai add here the shared fields
    static Status = {
        ACTIVE: "active",
        CANCELED: "canceled",
        UNPAID: "unpaid",
        REVOKED: "revoked",
        FINISHED: "finished",
    };

    isFinished(): boolean {
        return this.status === RecurringPaymentStoreObject.Status.FINISHED;
    }

    isCanceled(): boolean {
        return this.status === RecurringPaymentStoreObject.Status.CANCELED;
    }

    isUnpaid(): boolean {
        return this.status === RecurringPaymentStoreObject.Status.UNPAID;
    }

    isActive(): boolean {
        // TODO: investigate where this is used, should depend on the status probably
        return this.getActiveUntil() > new StemDate();
    }

    isActiveButCanceled(): boolean {
        return this.isActive() && this.isCanceled();
    }

    // TODO @Mihai deprecate
    getPaidUntil(): StemDate {
        return StemDate.optionally(this.paidUntil);
    }

    getActiveUntil(): StemDate {
        return StemDate.optionally(this.activeUntil);
    }

    hasGracePeriod(): boolean {
        const paidUntil = this.getPaidUntil();
        const activeUntil = this.getActiveUntil();

        return !paidUntil.equals(activeUntil);
    }

    getNextBillingDate(): StemDate {
        return StemDate.optionally(this.nextPaymentAttempt);
    }

    getLastPaymentDate(): StemDate {
        return StemDate.optionally(this.lastSuccessfulPayment);
    }

    getCurrency(): any {
        return this.currency;
    }

    getPrice(): Money {
        return new Money(this.amount, this.currency);
    }

    getMerchant(): Merchant | null {
        return Merchant.get(this.merchantId);
    }

    getMerchantTitle(): string | undefined {
        return this.getMerchant()?.getName();
    }

    getPaymentMethod(): PaymentMethod | null {
        if (this.preferredPaymentMethodId) {
            return PaymentMethod.get(this.preferredPaymentMethodId);
        }
        return PaymentMethod.getPrimary();
    }

    formatPrice(): string {
        return "";
    }

    getAvailablePaymentMethods(): PaymentMethod[] {
        const cards = [
            ...PaymentMethod.getPaymentMethodsForRecurringPayment(this),
            ...PaymentMethod.getAvailablePaymentMethods()
        ];
        return cards.filter((card, index) => cards.indexOf(card) === index);
    }

    getShippingAddress(): UserAddress {
        return this.deliveryAddress;
    }

    getBillingAddress(): UserAddress {
        return this.billingAddress || this.deliveryAddress;
    }
}
